import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, KeyRound, RefreshCcw, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase QCM — FMCSA WebKey settings card (super admin only).
 *
 * Shows:
 *   * Status chip (Configured / Using env var / Not configured).
 *   * Masked key + last-rotated relative date.
 *   * Input + Save to rotate.
 *   * Test connection (hits /admin/fmcsa/snapshot/1597181).
 *   * Clear with confirmation (falls back to env var).
 */
function relTime(iso) {
    if (!iso) return null;
    try {
        const ms = Math.max(0, Date.now() - new Date(iso).getTime());
        const m = Math.floor(ms / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
    } catch { return null; }
}

export default function FmcsaSettingsCard() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings/fmcsa");
            setStatus(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onSave = async () => {
        const trimmed = pending.trim();
        if (trimmed.length < 10) {
            toast.error("WebKey must be at least 10 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.put("/admin/settings/fmcsa", { web_key: trimmed });
            setPending("");
            toast.success("FMCSA WebKey rotated.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const onTest = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/fmcsa/snapshot/1597181");
            toast.success(`Test OK — pulled ${data.legal_name || "carrier"} (DOT ${data.dot_number}).`);
        } catch (e) {
            toast.error("Test failed: " + formatApiError(e));
        } finally { setBusy(false); }
    };

    const onClear = async () => {
        if (!window.confirm("Clear the FMCSA WebKey? The integration will fall back to the FMCSA_WEB_KEY env var if set.")) return;
        setBusy(true);
        try {
            await api.delete("/admin/settings/fmcsa");
            toast.success("FMCSA WebKey cleared.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const cfg = status?.configured;
    const source = status?.source;
    const chip = !cfg
        ? { tone: "amber", label: "Not configured", Icon: AlertTriangle }
        : source === "env"
            ? { tone: "slate", label: "Using env var", Icon: KeyRound }
            : { tone: "emerald", label: "Configured", Icon: CheckCircle2 };
    const toneCls = {
        amber: "bg-amber-50 text-amber-900 border-amber-200",
        slate: "bg-slate-100 text-slate-700 border-slate-200",
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    }[chip.tone];

    return (
        <Card data-testid="fmcsa-settings-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <KeyRound size={16} className="text-blue-900" />
                    FMCSA QCMobile WebKey
                </CardTitle>
                <CardDescription>
                    Rotate the FMCSA QCMobile API webKey used for carrier lookups,
                    EIN auto-fill, and the FMCSA Snapshot panel. Stored encrypted in
                    <code className="text-xs bg-slate-100 px-1 mx-1 rounded">system_settings</code>;
                    falls back to the <code className="text-xs bg-slate-100 px-1 mx-1 rounded">FMCSA_WEB_KEY</code> env var if cleared.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="flex items-center gap-3 flex-wrap">
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${toneCls}`}
                        data-testid="fmcsa-settings-status-chip"
                    >
                        <chip.Icon size={12} /> {chip.label}
                    </span>
                    {cfg && status?.masked_key && (
                        <code
                            className="text-xs text-slate-700 bg-slate-50 border border-slate-200 px-2 py-1 rounded"
                            data-testid="fmcsa-settings-masked-key"
                        >{status.masked_key}</code>
                    )}
                    {status?.last_rotated_at && (
                        <span className="text-xs text-slate-500">
                            Rotated {relTime(status.last_rotated_at)}
                        </span>
                    )}
                    {loading && <span className="text-xs text-slate-400">Loading…</span>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="fmcsa-webkey">Set / rotate webKey</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                            id="fmcsa-webkey"
                            data-testid="fmcsa-settings-input"
                            type="password"
                            autoComplete="off"
                            placeholder="Paste a new FMCSA QCMobile webKey"
                            value={pending}
                            onChange={(e) => setPending(e.target.value)}
                            disabled={busy}
                        />
                        <Button
                            onClick={onSave}
                            disabled={busy || !pending.trim()}
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid="fmcsa-settings-save"
                        >
                            <KeyRound size={14} /> Save
                        </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                        Get a webKey at <a className="text-blue-900 underline" href="https://mobile.fmcsa.dot.gov/developer" target="_blank" rel="noreferrer">mobile.fmcsa.dot.gov/developer</a>.
                    </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
                    <Button
                        variant="outline" size="sm"
                        disabled={busy || !cfg}
                        onClick={onTest}
                        className="gap-1.5"
                        data-testid="fmcsa-settings-test"
                    >
                        <RefreshCcw size={14} /> Test connection
                    </Button>
                    <Button
                        variant="ghost" size="sm"
                        disabled={busy || !cfg || source === "env"}
                        onClick={onClear}
                        className="text-red-700 hover:text-red-800 gap-1.5"
                        data-testid="fmcsa-settings-clear"
                    >
                        <Trash2 size={14} /> Clear stored key
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
